import { verifySignature } from './crypto';
import { hashStringAsync } from './canonical';
import type { MessageBase, Signature } from '../types/message';

/**
 * Verify a message's canonical hash.
 */
export async function verifyMessageHash(
  message: MessageBase,
  algo: string = 'sha256',
): Promise<boolean> {
  try {
    const { hash, signatures: _signatures, cles_de_chiffrement: _cles_de_chiffrement, ...canonical } = message;
    const calculatedHash = await hashStringAsync(
      JSON.stringify(canonical),
      algo,
    );
    return hash?.hash_value === calculatedHash;
  } catch {
    return false;
  }
}

/**
 * Verify all signatures of a message (crypto only).
 */
export function verifyMessageSignatures(
  message: MessageBase,
  signatures: Signature[],
): {
  valid: Signature[];
  invalid: Signature[];
} {
  const valid: Signature[] = [];
  const invalid: Signature[] = [];

  for (const sig of signatures) {
    if (sig.hash !== message.hash?.hash_value) {
      invalid.push(sig);
      continue;
    }

    const messageToVerify = `${sig.hash}-${sig.nonce}`;
    const isValid = verifySignature(
      messageToVerify,
      sig.signature,
      sig.cle_publique,
    );

    if (isValid) {
      valid.push(sig);
    } else {
      invalid.push(sig);
    }
  }

  return { valid, invalid };
}

/**
 * Filter signatures to those whose cle_publique is in allowedPubkeys.
 * Use after crypto verification to enforce "clé autorisée" (validators).
 */
export function filterSignaturesByAuthorizedPubkeys(
  signatures: Signature[],
  allowedPubkeys: Set<string>,
): { authorized: Signature[]; unauthorized: Signature[] } {
  const authorized: Signature[] = [];
  const unauthorized: Signature[] = [];
  for (const sig of signatures) {
    if (allowedPubkeys.has(sig.cle_publique)) {
      authorized.push(sig);
    } else {
      unauthorized.push(sig);
    }
  }
  return { authorized, unauthorized };
}

/**
 * Verify signatures: crypto then filter by allowed pubkeys.
 * Returns valid only if both crypto-valid and authorized.
 */
export function verifyMessageSignaturesStrict(
  message: MessageBase,
  signatures: Signature[],
  allowedPubkeys: Set<string>,
): {
  valid: Signature[];
  invalid: Signature[];
  unauthorized: Signature[];
} {
  const { valid: cryptoValid, invalid } = verifyMessageSignatures(
    message,
    signatures,
  );
  const { authorized, unauthorized } = filterSignaturesByAuthorizedPubkeys(
    cryptoValid,
    allowedPubkeys,
  );
  return {
    valid: authorized,
    invalid: [...invalid, ...unauthorized],
    unauthorized,
  };
}

/**
 * Check if a nonce has been used before (anti-replay).
 */
export class NonceCache {
  private cache: Map<string, number> = new Map();
  private ttl: number;

  constructor(ttlMs: number = 3600000) {
    this.ttl = ttlMs;
  }

  /**
   * Check if a nonce is valid (not seen before in TTL window).
   */
  isValid(nonce: string, timestamp: number): boolean {
    const now = Date.now();
    const cached = this.cache.get(nonce);

    if (cached !== undefined) {
      if (now - cached < this.ttl) {
        return false;
      }
      this.cache.delete(nonce);
    }

    this.cache.set(nonce, timestamp);
    this.cleanup(now);
    return true;
  }

  /**
   * Clean up expired entries.
   */
  private cleanup(now: number): void {
    for (const [nonce, timestamp] of this.cache.entries()) {
      if (now - timestamp >= this.ttl) {
        this.cache.delete(nonce);
      }
    }
  }

  /**
   * Clear all entries.
   */
  clear(): void {
    this.cache.clear();
  }
}

/**
 * Verify timestamp is within acceptable window.
 */
export function verifyTimestamp(
  timestamp: number,
  windowMs: number = 300000,
): boolean {
  const now = Date.now();
  const diff = Math.abs(now - timestamp);
  return diff <= windowMs;
}
